import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import {
    getPageLinkByNodeId,
    LinkModel,
    useGetLink,
    useGetLinks,
} from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import baseTheme, { BaseTheme } from "@insite/mobius/globals/baseTheme";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import * as cssLinter from "css";
import * as React from "react";
import { FC } from "react";
import { css } from "styled-components";

const enum fields {
    direction = "direction",
    alignment = "alignment",
    image = "image",
    links = "links",
}

interface LinkModelExtended {
    fields: {
        openInNewWindow: boolean;
        overriddenTitle: string;
        destination: LinkFieldValue;
        image: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.direction]: "vertical" | "horizontal";
        [fields.alignment]: "left" | "center" | "right";
        [fields.image]: string;
        [fields.links]: LinkModelExtended[];
    };
    extendedStyles?: LinkListStyles;
}

export interface LinkListStyles {
    containerWrapper?: InjectableCss;
    image?: LazyImageProps;
    title?: TypographyPresentationProps;
    linkWrapper?: InjectableCss;
    linkListStyle?: InjectableCss;
}

export const linkListStyles: LinkListStyles = {
    containerWrapper: {
        css: css`
            display: flex;
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    css`
                        justify-content: center;
                    `,
                    null,
                    css`
                        justify-content: center;
                    `,
                    css`
                        justify-content: flex-end;
                    `,
                ],
                "min",
            )}
        `,
    },
    linkListStyle: {
        css: css`
            font-size: ${TYPOGRAPHY.size.smaller};
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            line-height: 16px;
            span {
                color: ${COLORS.link.base} !important;
            }
        `,
    },
    linkWrapper: {
        css: css`
            &:not(:last-child) {
                padding-right: 44px;
            }
        `,
    },
    image: {
        css: css`
            padding-right: 4px;
        `,
    },
};

export const FooterSalesmapQuoteList: FC<OwnProps> = ({ fields, extendedStyles }) => {
    const { links } = useGetLinks(fields.links, o => o.fields.destination);
    const styles = useMergeStyles("linkList", linkListStyles, extendedStyles);

    const alignmentStyles = {
        css: css`
            text-align: ${fields.alignment || "right"};
        `,
    };

    const directionStyles = {
        css: css`
            ${fields.direction === "horizontal" ? "display: inline-block;" : "padding: 1px 0 0 0;"};
            ${styles.linkWrapper?.css}
        `,
    };

    return (
        <StyledWrapper {...styles.containerWrapper}>
            <StyledWrapper className="list-wrapper" {...alignmentStyles}>
                {links.map((list, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <StyledWrapper className="links-wrapper" key={index} {...directionStyles}>
                        <LazyImage src={fields.links[index].fields.image} {...styles.image} altText={fields.links[index].fields.overriddenTitle || list.title} />
                        {list.url && (
                            <Link
                                className="list-link"
                                href={list.url}
                                target={fields.links[index].fields.openInNewWindow ? "_blank" : ""}
                                {...styles.linkListStyle}>
                                {fields.links[index].fields.overriddenTitle || list.title}
                            </Link>
                        )}
                    </StyledWrapper>
                ))}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const widgetModule: WidgetModule = {
    component: FooterSalesmapQuoteList,
    definition: {
        group: "Basic",
        icon: "ax-link-list",
        fieldDefinitions: [
            {
                name: fields.direction,
                editorTemplate: "DropDownField",
                defaultValue: "vertical",
                fieldType: "General",
                options: [
                    {
                        displayName: "Vertical",
                        value: "vertical",
                    },
                    {
                        displayName: "Horizontal",
                        value: "horizontal",
                    },
                ],
                hideEmptyOption: true,
                tab: basicTab,
            },
            {
                name: fields.alignment,
                editorTemplate: "DropDownField",
                defaultValue: "left",
                fieldType: "General",
                options: [
                    {
                        displayName: "Left",
                        value: "left",
                    },
                    {
                        displayName: "Center",
                        value: "center",
                    },
                    {
                        displayName: "Right",
                        value: "right",
                    },
                ],
                hideEmptyOption: true,
                tab: basicTab,
            },
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const {
                        destination: { type, value },
                        overriddenTitle,
                    } = item.fields;
                    if (overriddenTitle) {
                        return overriddenTitle;
                    }
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Category") {
                        let displayValue = "";
                        const categories = (state as any).pageEditor.categories;
                        if (!categories) {
                            displayValue = "Loading...";
                        } else {
                            const matchingCategories = categories.filter((o: { id: string }) => o.id === value);
                            if (matchingCategories.length === 0) {
                                displayValue = "Unknown";
                            } else {
                                displayValue = matchingCategories[0].shortDescription;
                            }
                        }

                        return displayValue;
                    }
                    if (type === "Url") {
                        return value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                    },
                    {
                        name: "image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },
                    {
                        name: "overriddenTitle",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
                tab: basicTab,
            },
        ],
    },
};

export default widgetModule;
